/**
 * Comment utilities for Comments component
 * Handles comment sorting, formatting, and manipulation
 */

/**
 * Sorts comments based on the specified criteria
 * @param {Array} comments - Array of comments to sort
 * @param {string} sortBy - Sorting criteria ('top', 'newest', 'oldest')
 * @returns {Array} Sorted comments array
 */
export const sortComments = (comments, sortBy) => {
  const sortedComments = [...comments];

  switch (sortBy) {
    case 'top':
      return sortedComments.sort((a, b) => {
        const upvotesA = a.upvotes || 0;
        const upvotesB = b.upvotes || 0;
        if (upvotesA !== upvotesB) {
          return upvotesB - upvotesA;
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });

    case 'newest':
      return sortedComments.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );

    case 'oldest':
      return sortedComments.sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
      );

    default:
      return sortedComments;
  }
};

/**
 * Formats timestamp into human-readable format
 * @param {string|number|Date} timestamp - Timestamp to format
 * @returns {string} Formatted timestamp string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return 'Unknown time';
  }

  let date;

  try {
    if (timestamp instanceof Date) {
      date = timestamp;
    }
    else if (typeof timestamp === 'number') {
      date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp);
    }
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    else if (timestamp.timestamp) {
      date = new Date(timestamp.timestamp);
    }
    else {
      date = new Date(timestamp);
    }
  } catch (error) {
    console.error('Error parsing timestamp:', timestamp, error);
    return 'Invalid date';
  }

  if (isNaN(date.getTime())) {
    console.error('Invalid timestamp:', timestamp);
    return 'Invalid date';
  }

  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);
  const diffInMinutes = (now - date) / (1000 * 60);

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)}m ago`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else {
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
};

/**
 * Gets all comment IDs from a nested comment structure
 * @param {Array} comments - Array of comments (can include nested replies)
 * @returns {Array} Array of all comment IDs
 */
export const getAllCommentIds = (comments) => {
  let allIds = [];
  comments.forEach(comment => {
    allIds.push(comment.id);
    if (comment.replies && comment.replies.length > 0) {
      allIds = allIds.concat(getAllCommentIds(comment.replies));
    }
  });
  return allIds;
};

/**
 * Gets all comments from a nested comment structure
 * @param {Array} comments - Array of comments (can include nested replies)
 * @returns {Array} Array of all comments flattened
 */
export const getAllComments = (comments) => {
  let allComments = [];
  comments.forEach(comment => {
    allComments.push(comment);
    if (comment.replies && comment.replies.length > 0) {
      allComments = allComments.concat(getAllComments(comment.replies));
    }
  });
  return allComments;
};

/**
 * Calculates the depth of a comment in the reply chain
 * @param {number} commentId - ID of the comment to check
 * @param {Array} comments - Array of comments to search through
 * @param {number} currentDepth - Current depth level (used in recursion)
 * @returns {number} Depth of the comment (-1 if not found)
 */
export const getCommentDepth = (commentId, comments, currentDepth = 0) => {
  if (currentDepth >= 5) return 5;

  for (const c of comments) {
    if (c.id === commentId) return currentDepth;
    if (c.replies) {
      const depth = getCommentDepth(commentId, c.replies, currentDepth + 1);
      if (depth !== -1) return depth;
    }
  }
  return -1;
};

/**
 * Extracts user ID from user data object
 * @param {Object} userData - User data object
 * @returns {string|number|null} User ID or null if not found
 */
export const extractUserId = (userData) => {
  if (!userData) return null;
  return userData.id || userData.user_id || userData.userId || null;
};

/**
 * Validates comment content before submission
 * @param {string} content - Comment content to validate
 * @returns {Object} Validation result with isValid boolean and message string
 */
export const validateCommentContent = (content) => {
  if (!content || !content.trim()) {
    return { isValid: false, message: 'Please enter a comment before submitting.' };
  }
  
  if (content.trim().length < 3) {
    return { isValid: false, message: 'Comment must be at least 3 characters long.' };
  }
  
  return { isValid: true, message: '' };
};
